import { Avatar, Col, Sidepanel, Spinner2 } from 'components';
import { Api, capitalize, formatLocale } from 'lib';
import { theme } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentHeader } from 'screens/Admin/components';
import { formatAmount, formatDate, STATUS_STYLES } from './helpers';
import BillingTimeline from 'screens/Billing/components/BillingTimeline';
import TimelineEvent from './TimelineEvent';

const StatusBadge = ({ status, label }) => {
  const normalizedStatus = status === 'success' ? 'active' : status;
  const s = STATUS_STYLES[normalizedStatus] || STATUS_STYLES.pending;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-md border ${s.bg} ${s.text} ${s.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {label || capitalize(normalizedStatus || '')}
    </span>
  );
};

const InfoRow = ({ label, children, muted }) => (
  <div className="flex items-baseline justify-between py-[5px]">
    <span className="text-[12px] text-[#8993a4] select-none">{label}</span>
    <span
      className={`text-[13px] font-medium ${muted ? 'text-[#8993a4]' : 'text-[#2d3643]'}`}
    >
      {children}
    </span>
  </div>
);

const IdRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between py-[5px] gap-3">
      <span className="text-[12px] text-[#8993a4] select-none">{label}</span>
      <span className="text-[12px] font-medium text-[#2d3643] text-right break-all font-mono">
        {String(value)}
      </span>
    </div>
  );
};

const WarnRow = ({ label, children, color = 'text-red-500' }) => (
  <div className="flex items-baseline justify-between py-[5px]">
    <span className="text-[12px] text-[#8993a4]">{label}</span>
    <span className={`text-[13px] font-medium ${color}`}>{children}</span>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold text-[#8993a4] uppercase tracking-[0.06em]">
    {children}
  </p>
);

const NoteBlock = ({ label, children }) => (
  <div className="py-2 space-y-1">
    <span className="text-[11px] font-semibold text-[#8993a4] uppercase tracking-[0.06em] select-none">{label}</span>
    <p className="text-[13px] text-[#2d3643] leading-relaxed whitespace-pre-wrap break-words">{children}</p>
  </div>
);

const SubscriberPanel = ({ subscriptionId, closeModal }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!subscriptionId) {
      setData(null);
      return;
    }
    fetchDetail();
  }, [subscriptionId]);

  async function fetchDetail() {
    setLoading(true);
    setData(null);
    const res = await Api.post('/admin/subscriptionPlans/subscriberDetail', {
      subscriptionId
    });
    if (res?.data) {
      setData(res.data);
    }
    setLoading(false);
  }

  const user = data?.user;
  const team = data?.team;
  const sub = data?.subscription;
  const events = data?.events || [];
  const plan = sub?.plan;
  const pm = sub?.paymentMethod;
  const refundRequest = data?.refundRequest;

  async function handleApproveRefund() {
    if (!refundRequest?._id) return;
    setRefundLoading(true);
    const res = await Api.post('/admin/billing/approveRefund', {
      refundRequestId: refundRequest._id,
      reason: rejectReason || undefined
    });
    if (res) { setRejectReason(''); fetchDetail(); }
    setRefundLoading(false);
  }

  async function handleRejectRefund() {
    if (!refundRequest?._id) return;
    setRefundLoading(true);
    const res = await Api.post('/admin/billing/rejectRefund', {
      refundRequestId: refundRequest._id,
      reason: rejectReason || undefined
    });
    if (res) { setRejectReason(''); fetchDetail(); }
    setRefundLoading(false);
  }

  return (
    <Sidepanel wid={600} pad="0" visible={!!subscriptionId} closeModal={closeModal}>
      <ContentHeader
        text={user ? user.name || user.username : ''}
        closePanel={closeModal}
      />

      {loading && (
        <Col
          zIndex={5}
          pos="absolute"
          ht="100%"
          wid="100%"
          bg="rgba(210, 216, 223, 0.7)"
          left="0"
          centerAll
        >
          <Spinner2 col={theme.SECONDARY} />
        </Col>
      )}

      {data && user && sub && (
        <div className="px-6 py-5 space-y-5">
          {/* ── User + Team header ────────────────────────── */}
          <div className="flex items-center gap-3.5">
            <Avatar user={user} avatar={user.avatar} small />
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-[#2d3643] truncate">
                {user.name || user.username}
              </p>
              <p className="text-[13px] text-[#8993a4] truncate">{user.email}</p>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {user.type && (
                  <span className="text-[11px] text-[#6b7685] bg-[#f1f3f5] px-2 py-0.5 rounded font-medium">
                    {capitalize(user.type)}
                  </span>
                )}
                {team && (
                  <span className="flex items-center gap-1.5 text-[11px] text-[#6b7685] bg-[#f1f3f5] px-2 py-0.5 rounded font-medium">
                    {team.avatar && (
                      <img
                        src={team.avatar}
                        alt=""
                        className="w-3.5 h-3.5 rounded-full object-cover"
                      />
                    )}
                    {team.name}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Subscription card ─────────────────────────── */}
          <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden">
            {/* Header with amount */}
            <div className="px-5 pt-4 pb-3.5 border-b border-[#f0f2f4]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[13px] font-semibold text-[#2d3643]">
                    {plan?.title || '-'}
                  </p>
                  {sub.amount != null && (
                    <p className="text-[22px] font-bold text-[#2d3643] mt-1 tabular-nums tracking-tight leading-none">
                      {formatAmount(sub.amount, sub.currency)}
                      <span className="text-[13px] font-normal text-[#8993a4] ml-0.5">
                        /{t(
                          `subscriptions.admin.subscribers.detail.intervalShort.${sub.interval === 'monthly' ? 'monthly' : 'yearly'}`
                        )}
                      </span>
                    </p>
                  )}
                </div>
                <StatusBadge status={sub.status} />
              </div>
            </div>

            {/* Details */}
            <div className="px-5 py-3">
              <InfoRow label={t('subscriptions.admin.subscribers.provider')}>
                {capitalize(sub.provider || '')}
              </InfoRow>
              <InfoRow label={t('subscriptions.admin.subscribers.detail.interval')}>
                {capitalize(sub.interval || '')}
              </InfoRow>

              <div className="h-px bg-[#f0f2f4] my-2" />

              <InfoRow label={t('subscriptions.admin.subscribers.detail.started')}>
                {formatDate(sub.startedAt)}
              </InfoRow>
              <InfoRow label={t('subscriptions.admin.subscribers.detail.currentPeriodStart')}>
                {formatDate(sub.currentPeriodStart)}
              </InfoRow>
              <InfoRow label={t('subscriptions.admin.subscribers.detail.currentPeriodEnd')}>
                {formatDate(sub.currentPeriodEnd)}
              </InfoRow>
              {sub.nextRenewalAt && (
                <InfoRow label={t('subscriptions.admin.subscribers.detail.nextRenewal')}>
                  {formatDate(sub.nextRenewalAt)}
                </InfoRow>
              )}

              {(sub.renewalCount > 0 ||
                sub.renewalFailureCount > 0 ||
                sub.paymentFailureCount > 0 ||
                sub.canceledAt ||
                sub.endedAt) && <div className="h-px bg-[#f0f2f4] my-2" />}

              {sub.renewalCount > 0 && (
                <InfoRow label={t('subscriptions.admin.subscribers.detail.renewals')}>
                  {sub.renewalCount}
                </InfoRow>
              )}
              {(sub.renewalFailureCount > 0 || sub.paymentFailureCount > 0) && (
                <WarnRow label={t('subscriptions.admin.subscribers.detail.failures')}>
                  {sub.renewalFailureCount || 0} renewal / {sub.paymentFailureCount || 0}{' '}
                  payment
                </WarnRow>
              )}
              {sub.canceledAt && (
                <WarnRow label={t('subscriptions.admin.subscribers.detail.cancelledAt')}>
                  {formatDate(sub.canceledAt)}
                </WarnRow>
              )}
              {sub.cancelAt && !sub.canceledAt && (
                <WarnRow
                  label={t('subscriptions.admin.subscribers.detail.scheduledCancel')}
                  color="text-amber-600"
                >
                  {formatDate(sub.cancelAt)}
                </WarnRow>
              )}
              {sub.endedAt && (
                <WarnRow label={t('subscriptions.admin.subscribers.detail.endedAt')}>
                  {formatDate(sub.endedAt)}
                </WarnRow>
              )}
              {sub.cancellationReason && (
                <InfoRow label={t('subscriptions.admin.subscribers.detail.reason')} muted>
                  {sub.cancellationReason}
                </InfoRow>
              )}

              <div className="h-px bg-[#f0f2f4] my-2" />

              <InfoRow label={t('subscriptions.admin.subscribers.detail.created')} muted>
                {formatDate(sub.createdAt)}
              </InfoRow>

              <div className="h-px bg-[#f0f2f4] my-2" />

              <IdRow label="Subscription ID" value={sub._id} />
              <IdRow label="Provider Sub ID" value={sub.providerSubscriptionId} />
            </div>

            {/* Payment method */}
            {pm && (pm.brand || pm.type) && (
              <div className="px-5 py-3.5 bg-[#fafbfc] border-t border-[#f0f2f4]">
                <p className="text-[11px] font-semibold text-[#8993a4] uppercase tracking-[0.06em] mb-2.5">
                  {t('subscriptions.admin.subscribers.detail.paymentMethod')}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-7 rounded bg-[#e8ecf1] flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] font-bold text-[#8993a4] uppercase tracking-wide">
                      {pm.brand ? pm.brand.slice(0, 4) : pm.type?.slice(0, 4) || ''}
                    </span>
                  </div>
                  <div>
                    <p className="text-[13px] font-medium text-[#2d3643]">
                      {pm.brand ? capitalize(pm.brand) : capitalize(pm.type || '')}
                      {pm.last4 && (
                        <span className="text-[#8993a4] ml-1.5">•••• {pm.last4}</span>
                      )}
                    </p>
                    {pm.expiryMonth && pm.expiryYear && (
                      <p className="text-[11px] text-[#8993a4]">
                        {t('subscriptions.admin.subscribers.detail.expiryShort')}{' '}
                        {String(pm.expiryMonth).padStart(2, '0')}/{pm.expiryYear}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Refund request ─────────────────────────────── */}
          {refundRequest && (
            <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden">
              <div className="px-5 pt-4 pb-3 border-b border-[#f0f2f4] flex items-center justify-between">
                <SectionLabel>{t('subscriptions.admin.subscribers.refund.title')}</SectionLabel>
                <StatusBadge status={refundRequest.status} />
              </div>
              <div className="px-5 py-3 space-y-0.5">
                <IdRow label={t('subscriptions.admin.subscribers.refund.requestId')} value={refundRequest._id} />
                <InfoRow label={t('subscriptions.admin.subscribers.refund.amount')}>
                  {refundRequest.amount} {refundRequest.currency?.toUpperCase()}
                </InfoRow>
                <InfoRow label={t('subscriptions.admin.subscribers.refund.provider')}>{capitalize(refundRequest.provider || '')}</InfoRow>
                {refundRequest.reason && (
                  <NoteBlock label={t('subscriptions.admin.subscribers.refund.userNote')}>{refundRequest.reason}</NoteBlock>
                )}
                <InfoRow label={t('subscriptions.admin.subscribers.refund.requested')} muted>
                  {formatLocale(refundRequest.createdAt, 'MMM d, yyyy')}
                </InfoRow>
                {refundRequest.updatedAt && (
                  <InfoRow label={t('subscriptions.admin.subscribers.refund.updated')} muted>
                    {formatLocale(refundRequest.updatedAt, 'MMM d, yyyy')}
                  </InfoRow>
                )}
                <IdRow label={t('subscriptions.admin.subscribers.refund.providerPaymentId')} value={refundRequest.providerPaymentId} />
                {refundRequest.status === 'approved' && refundRequest.approvalReason && (
                  <NoteBlock label={t('subscriptions.admin.subscribers.refund.approvalReason')}>{refundRequest.approvalReason}</NoteBlock>
                )}
                {refundRequest.status === 'rejected' && refundRequest.rejectionReason && (
                  <NoteBlock label={t('subscriptions.admin.subscribers.refund.rejectionReason')}>{refundRequest.rejectionReason}</NoteBlock>
                )}
                {refundRequest.status === 'approved' && refundRequest.providerRefundId && (
                  <IdRow label={t('subscriptions.admin.subscribers.refund.providerRefundId')} value={refundRequest.providerRefundId} />
                )}
                <InfoRow label={t('subscriptions.admin.subscribers.refund.processedBy')} muted>{refundRequest.processedByEmail || '-'}</InfoRow>
                <IdRow label={t('subscriptions.admin.subscribers.refund.subscriptionId')} value={sub?._id} />
                <IdRow label={t('subscriptions.admin.subscribers.refund.providerSubId')} value={sub?.providerSubscriptionId} />
              </div>

              {refundRequest.status === 'pending' && (
                <div className="px-5 pb-4 pt-1 border-t border-[#f0f2f4] space-y-2">
                  <textarea
                    rows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder={t('subscriptions.admin.subscribers.refund.adminMessagePlaceholder')}
                    className="w-full px-3 py-2 text-[13px] border border-[#e5e7eb] rounded-lg outline-none focus:border-[#b5bdc8] placeholder:text-[#b5bdc8] font-[CeraPro] resize-none leading-relaxed"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleApproveRefund}
                      disabled={refundLoading}
                      className="flex-1 h-9 rounded-lg bg-emerald-600 text-white text-[13px] font-semibold font-[CeraPro] hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {t('subscriptions.admin.subscribers.refund.approve')}
                    </button>
                    <button
                      onClick={handleRejectRefund}
                      disabled={refundLoading}
                      className="flex-1 h-9 rounded-lg bg-red-500 text-white text-[13px] font-semibold font-[CeraPro] hover:bg-red-600 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {t('subscriptions.admin.subscribers.refund.reject')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Event timeline ─────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-3.5">
              <SectionLabel>
                {t('subscriptions.admin.subscribers.detail.eventTimeline')}
              </SectionLabel>
              {events.length > 0 && (
                <span className="text-[11px] text-[#b5bdc8] tabular-nums">
                  {events.length === 1
                    ? t('subscriptions.admin.subscribers.detail.eventsCountOne', {
                        count: events.length
                      })
                    : t('subscriptions.admin.subscribers.detail.eventsCountOther', {
                        count: events.length
                      })}
                </span>
              )}
            </div>

            {data?.timeline?.entries?.length > 0 && (
              <div className="mb-4">
                <BillingTimeline timeline={data.timeline} audience="admin" />
              </div>
            )}

            {events.length > 0 ? (
              <details className="group">
                <summary className="cursor-pointer text-[12px] font-medium text-[#8993a4] select-none mb-2">
                  {t('subscriptions.admin.subscribers.detail.rawEvents', {
                    defaultValue: 'Raw event log'
                  })}
                </summary>
                <div className="bg-white border border-[#e5e7eb] rounded-xl px-5 py-4">
                  {events.map((event, i) => (
                    <TimelineEvent
                      key={event._id}
                      event={event}
                      isLast={i === events.length - 1}
                    />
                  ))}
                </div>
              </details>
            ) : (
              <div className="flex flex-col items-center text-center py-10 bg-white border border-[#e5e7eb] rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-[#f1f3f5] flex items-center justify-center mb-3">
                  <svg
                    className="w-5 h-5 text-[#b5bdc8]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <p className="text-[13px] text-[#8993a4]">
                  {t('subscriptions.admin.subscribers.detail.noEvents')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </Sidepanel>
  );
};

export default SubscriberPanel;
